import { useContext } from "react";
import * as Yup from "yup";
import inRange from "lodash/inRange";
import round from "lodash/round";
import pick from "lodash/pick";
import flatten from "lodash/flatten";

import { AssetsContext } from "../../context";
import {
  SHARED_VALIDATION_FIELDS,
  ASSET_ALLOCATION_FIELDS,
} from "../../utils/formFieldsForAssetType";
import {
  ASSET_ALLOCATION_LOWER_BOUND,
  ASSET_ALLOCATION_UPPER_BOUND,
} from "../../utils/assetServices";
import { INTEREST_RATE_CONTROL } from "./AssetFields";
import { parseNumberFunction } from "@utils/numberConversions";

const MAX_RISK_RATING = 0.05;

const MAX_MONEY_MARKET_RISK_RATING = 0.03;

export const setupValidations = () => {
  const { state } = useContext(AssetsContext);
  const {
    assets,
    asset,
    formAssetType,
    assumptions,
    groupedFormFields,
    userDetails,
    scenario,
  } = state;
  const { clientCurrentAge } = userDetails;
  const { expectedAge } = scenario;
  const formFields = Object.values(groupedFormFields);

  let schema = {};
  const assetNames = getAssetNames(assets, asset);
  const assetValidations = buildAssetValidations(
    formAssetType,
    assumptions,
    assetNames,
    expectedAge,
    clientCurrentAge
  );
  const validationFields = [
    ...flatten(formFields),
    ...SHARED_VALIDATION_FIELDS,
    ...ASSET_ALLOCATION_FIELDS,
  ];
  validationFields.forEach((field) => {
    schema[field] = assetValidations[field];
  });
  return Yup.object().shape(schema);
};

const getAssetNames = (assets, currentAsset = {}) => {
  let assetNames = assets.map((asset) => asset.name);
  const indexTorRemove = assetNames.indexOf(currentAsset.name);
  if (indexTorRemove > -1) {
    assetNames.splice(indexTorRemove, 1);
  }

  return assetNames;
};

const buildAssetValidations = (
  assetType,
  assumptions,
  assetNames,
  expectedAge,
  clientCurrentAge
) => {
  const maxInterestRate =
    getMaxInterestRateOnAssetType(assetType, assumptions) * 100;
  const assetValidations = {
    name: Yup.string()
      .required("A name for the asset is required")
      .typeError("A valid name for the asset is required")
      .notOneOf(assetNames, "A unique name for the asset is required"),
    contractNumber: Yup.string()
      .nullable()
      .typeError("A valid contract number for the asset is required")
      .matches(/^(?!0{1,})/, "Contract numbers cannot start with 0 values."),
    gross: Yup.number()
      .nullable()
      .typeError("A numeric value for the gross value is required"),
    assetCurrency: Yup.mixed().required("A currency for the asset is required"),
    monthlyContribution: Yup.number()
      .nullable()
      .typeError("A numeric value for the monthly contribution is required"),
    employerMonthlyContribution: Yup.number()
      .nullable()
      .typeError("A numeric value for the monthly contribution is required"),
    monthlyIncome: Yup.number()
      .nullable()
      .typeError("A numeric value for the monthly income is required"),
    interestRate: Yup.number()
      .required(interestRateRequiredMessage(assetType))
      .max(maxInterestRate, interestRateMaxMessage(assetType, maxInterestRate))
      .min(
        getMinInterestRateOnAssetType(assetType),
        interestRateMinMessage(assetType)
      )
      .typeError("A numeric value for the interest rate is required"),
    liability: Yup.number()
      .nullable()
      .typeError("A numeric value for the liability is required"),
    termOfDebt: Yup.number().when("liability", {
      is: (liability) => isFinite(liability) && liability > 0,
      then: (schema) =>
        schema
          .required("A term of debt for the asset is required")
          .min(1, "This is not a realistic term of debt")
          .max(100, "This is not a realistic term of debt")
          .typeError("A numeric value for the term of debt is required"),
      otherwise: (schema) =>
        schema
          .nullable()
          .max(100, "This is not a realistic term of debt")
          .typeError("A numeric value for the term of debt is required"),
    }),
    monthlyProfit: Yup.number()
      .nullable()
      .typeError("A numeric value for the monthly profit is required"),
    baseCost: Yup.number()
      .nullable()
      .typeError("A numeric value for the total past contribution is required"),
    interestRateControl: Yup.number(),
    allocationTotal: Yup.number().when("interestRateControl", {
      is: INTEREST_RATE_CONTROL.BASED_ON_ASSET_ALLOCATION,
      then: (schema) =>
        schema.test(
          "maxAllocations",
          `Your total allocations don't seem to add up to 100%`,
          maxAllocationsTest
        ),
      otherwise: (schema) => schema,
    }),
    changeGrossIncomeEvent: Yup.object().shape({
      payload: Yup.object().when("active", {
        is: true,
        then: (schema) =>
          schema.shape({
            value: Yup.number()
              .nullable()
              .typeError("A numeric value for the gross income is required"),
          }),
      }),
    }),
    changeMonthlyContributionEvent: Yup.object().shape({
      payload: Yup.object().shape({
        value: Yup.number()
          .nullable()
          .typeError("A numeric value for the contribution is required")
          .min(0, "You can't have a negative contribution value"),
      }),
    }),
    matureRetirementAssetEvent: Yup.object().shape({
      when: Yup.object().when("active", {
        is: true,
        then: (schema) =>
          schema.shape({
            value: Yup.number()
              .nullable()
              .required("An age for this event is required")
              .typeError("A numeric value for the gross income is required")
              .min(clientCurrentAge, "The age can't be in the past")
              .max(
                expectedAge,
                `The forecast is set to project until the age of ${expectedAge}`
              ),
          }),
      }),
    }),
    potBreakdown: Yup.object().shape({
      when: Yup.object().when("isTwoPot", {
        is: true,
        then: (schema) =>
          schema.shape({
            vestedPot: Yup.object().shape({
              gross_value: Yup.number()
                .nullable()
                .typeError("A numeric value is required"),
            }),
            retirementPot: Yup.object().shape({
              gross_value: Yup.number()
                .nullable()
                .typeError("A numeric value is required"),
            }),
            savingsPot: Yup.object().shape({
              gross_value: Yup.number()
                .nullable()
                .typeError("A numeric value is required"),
              monthly_income: Yup.number()
                .nullable()
                .typeError("A numeric value is required")
                .test(
                  "minSavingsPotWithdrawal",
                  `issues`,
                  minSavingsPotWithdrawalTest
                ),
            }),
          }),
      }),
    }),
  };
  return assetValidations;
};

const getMaxInterestRateOnAssetType = (assetType, assumptions) => {
  const inflation = assumptions.inflation;
  if (assetType === "money_market") {
    return inflation + MAX_MONEY_MARKET_RISK_RATING;
  } else if (assetType === "investment_property") {
    return 1;
  } else {
    return inflation + MAX_RISK_RATING;
  }
};

const getMinInterestRateOnAssetType = (assetType) => {
  if (assetType === "other_income") {
    return 0;
  }
  return 1;
};

const interestRateRequiredMessage = (assetType) => {
  if (assetType === "investment_property") {
    return "A bond interest rate for the asset is required";
  }
  return "An interest rate value for the asset is required";
};

const interestRateMaxMessage = (assetType, maxInterestRate) => {
  if (assetType === "money_market") {
    return `${maxInterestRate}% is the maximum realistic interest rate for a money market asset`;
  } else if (assetType === "investment_property") {
    return "This is not a realistic bond interest rate";
  } else {
    return "This is not a realistic return rate for this asset";
  }
};

const interestRateMinMessage = (assetType) => {
  if (assetType === "investment_property") {
    return "Your bond interest rate should be expressed as a percentage (e.g 10.5)";
  }
  return "Your return rate should expressed as a percentage (e.g 6.5)";
};

// The test functions from yup are called with a context bound to 'this'
function maxAllocationsTest() {
  const assetAllocations = pick(this.parent, [
    "domesticEquity",
    "domesticProperty",
    "domesticBonds",
    "domesticCash",
    "offshoreEquity",
    "offshoreProperty",
    "offshoreBonds",
    "offshoreCash",
  ]);

  const sumOfAssetAllocations = sumAssetAllocations(assetAllocations);
  const { path, createError } = this;

  const error = createError({
    path,
    message: `Your asset allocation total is ${round(
      sumOfAssetAllocations,
      2
    )}%, the total should be 100%`,
  });
  const valid = inRange(
    sumOfAssetAllocations,
    ASSET_ALLOCATION_LOWER_BOUND,
    ASSET_ALLOCATION_UPPER_BOUND
  );

  return valid || error;
}

function minSavingsPotWithdrawalTest() {
  const { createError } = this;
  const value = this.from[2]?.value.savingsPot?.annual_income;

  const valid = !(value > 0 && value < 2000);

  const error = createError({
    path: "potBreakdown.savingsPot.annual_income",
    message: `A minimum withdrawal amount of R 2000 is required if you are withdrawing from the savings pot.`,
  });
  return valid || error;
}

export const sumAssetAllocations = (assetAllocations) => {
  const parseNumber = parseNumberFunction({ zero: true });
  return Object.values(assetAllocations).reduce(
    (accumulator, allocation) => accumulator + parseNumber(allocation),
    0
  );
};
